import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Authenticated from "app/components/Wrappers/Authenticated";
import { Loading } from "app/components/Wrappers";
import ButtonVariant from "app/components/Button/ButtonVariant";
import coachnow_logo from "app/images/Logo_dark.svg";
import close_icon from "app/images/close-icon.svg";
import {
  trackWizardBackClicked,
  trackWizardCompleted,
  trackWizardExited,
  trackWizardStarted,
  trackWizardStepCompleted,
  trackWizardStepViewed,
  WizardStepId,
} from "app/utils/analytics-helpers/academySetup";
import {
  academyWizardSteps,
  ACADEMY_WIZARD_STEP_TOTAL,
} from "app/routes/AcademyWizard/steps";
import { AcademySetupLocationState } from "app/components/AcademySetup/items";
import { AcademyWizardLayoutProps } from "./index.types";
import AcademyWizardGuard from "./AcademyWizardGuard";
import styles from "./styles.module.scss";

// Seeded by the Welcome page's Begin Setup, or by whichever step a deep link
// enters without one; read and cleared when the wizard completes.
export const WIZARD_STARTED_AT_KEY = "academy-wizard-started-at";
export const WIZARD_BACK_COUNT_KEY = "academy-wizard-back-count";

/**
 * Ends the wizard session: a later run in this tab starts its own clock and
 * reports its own academy_wizard_started. Called on every deliberate exit —
 * Finish, Close, the final Skip, Back to Welcome and Welcome's Close — so a
 * later direct step URL is never mistaken for a continuation of an
 * abandoned run.
 */
export const clearWizardSession = (): void => {
  sessionStorage.removeItem(WIZARD_STARTED_AT_KEY);
  sessionStorage.removeItem(WIZARD_BACK_COUNT_KEY);
};
/** Every wizard exit (Finish or X) lands on the setup page. */
export const WIZARD_EXIT_STATE: AcademySetupLocationState = {
  setupEntryPoint: "wizard_complete",
};

const stepId = (index: number): WizardStepId =>
  index === 0 ? "facility_basics" : "coach_identity";

// Set before every deliberate exit (Next, Back, Skip, X). An unmount without it
// means the coach left another way, reported as abandoned. Module-level so it
// survives the unmount that reads it.
let leavingDeliberately = false;

const AcademyWizardLayoutContent = ({
  stepPath,
  title,
  subtitle,
  onBeforeNext,
  loading = false,
  nextLabel,
  collectStepStats,
  children,
}: AcademyWizardLayoutProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  // True while an async onBeforeNext (validation + save) is in flight; the
  // Next button shows its spinner and ignores further clicks meanwhile.
  const [saving, setSaving] = useState(false);

  const stepIndex = useMemo(
    () => academyWizardSteps.findIndex((step) => step.path === stepPath),
    [stepPath],
  );

  const mountedAt = useRef(Date.now());
  useEffect(() => {
    if (stepIndex < 0) {
      return undefined;
    }
    mountedAt.current = Date.now();
    leavingDeliberately = false;
    // A deep link into either step (the removed-step redirects land on the
    // first) never passed Welcome's Begin Setup, which starts the session and
    // reports it. Start and report it here, as a direct ("other") entry, so
    // the run has a started event and its completed event a real duration.
    if (!sessionStorage.getItem(WIZARD_STARTED_AT_KEY)) {
      sessionStorage.setItem(WIZARD_STARTED_AT_KEY, String(Date.now()));
      sessionStorage.setItem(WIZARD_BACK_COUNT_KEY, "0");
      trackWizardStarted("other");
    }
    trackWizardStepViewed(stepId(stepIndex), stepIndex + 1);

    // At most once per step view: pagehide and the cleanup can both run for
    // a single exit.
    let abandoned = false;
    const abandon = () => {
      if (abandoned) {
        return;
      }
      abandoned = true;
      trackWizardExited(stepId(stepIndex), stepIndex + 1, "abandoned");
    };
    // Closing the tab mid-step is an abandonment too.
    window.addEventListener("pagehide", abandon);
    return () => {
      window.removeEventListener("pagehide", abandon);
      if (!leavingDeliberately) {
        abandon();
      }
    };
  }, [stepIndex]);

  const step = academyWizardSteps[stepIndex];
  const prevStep = academyWizardSteps[stepIndex - 1];
  const nextStep = academyWizardSteps[stepIndex + 1];

  if (!step) {
    return null;
  }

  // Finishing the wizard lands on the setup page — the welcome copy promises
  // "a step-by-step guide" there — flagged as the entry point for its
  // setup_page_viewed event.
  const leaveToSetup = () => {
    leavingDeliberately = true;
    clearWizardSession();
    history.push("/academy/setup", WIZARD_EXIT_STATE);
  };

  const goToNextStep = async () => {
    if (onBeforeNext) {
      setSaving(true);
      try {
        const canAdvance = await onBeforeNext();
        if (!canAdvance) {
          return;
        }
      } catch (error) {
        console.error("Error saving wizard step:", error);
        toast.error(t("Something went wrong."));
        return;
      } finally {
        setSaving(false);
      }
    }
    const stats = collectStepStats
      ? collectStepStats()
      : { completed: 0, prefilled: 0, edited: 0 };
    trackWizardStepCompleted(
      stepId(stepIndex),
      stepIndex + 1,
      Date.now() - mountedAt.current,
      stats.completed,
      stats.prefilled,
      stats.edited,
    );
    if (nextStep) {
      leavingDeliberately = true;
      history.push(nextStep.path);
      return;
    }
    trackWizardCompleted(
      Date.now() -
        Number(sessionStorage.getItem(WIZARD_STARTED_AT_KEY) || Date.now()),
      Number(sessionStorage.getItem(WIZARD_BACK_COUNT_KEY) || 0),
    );
    leaveToSetup();
  };

  // Step-to-step Back is tracked and counted; Back on the first step returns
  // to the Welcome page, which is navigation out of the steps, not a
  // step-to-step back — no academy_wizard_back_clicked for it.
  const goBack = () => {
    leavingDeliberately = true;
    if (prevStep) {
      trackWizardBackClicked(stepId(stepIndex), stepId(stepIndex - 1));
      sessionStorage.setItem(
        WIZARD_BACK_COUNT_KEY,
        String(Number(sessionStorage.getItem(WIZARD_BACK_COUNT_KEY) || 0) + 1),
      );
      history.push(prevStep.path);
      return;
    }
    // Begin Setup starts a fresh session; until then there is none.
    clearWizardSession();
    history.push("/academy/wizard");
  };

  // O1: skip advances without validating or saving; no analytics event is
  // defined for skip — do not invent one.
  const skip = () => {
    if (nextStep) {
      leavingDeliberately = true;
      history.push(nextStep.path);
      return;
    }
    leaveToSetup();
  };

  const canAdvance =
    Boolean(nextStep || onBeforeNext) && !saving && !loading;

  // Enter in a text field submits the step the way Next does. Implicit
  // submission needs a submit button in the form, so the body carries a hidden
  // one; the dropdowns preventDefault their own Enter, so picking an option
  // never advances the step.
  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (canAdvance) {
      goToNextStep();
    }
  };

  const counter = t("{{current}} of {{total}}", {
    current: stepIndex + 1,
    total: ACADEMY_WIZARD_STEP_TOTAL,
  });

  return (
    <div className={styles.page}>
      <header className={styles.top_bar}>
        <img src={coachnow_logo} alt="CoachNow" className={styles.logo} />
        <button
          type="button"
          className={styles.close_button}
          aria-label={t("Close")}
          // Disabled while saving, like Back: the overlay only blocks
          // pointers, and an in-flight save must not push the next step
          // over an exit already taken.
          disabled={saving}
          onClick={() => {
            trackWizardExited(
              stepId(stepIndex),
              stepIndex + 1,
              "close_button",
            );
            leaveToSetup();
          }}
        >
          <img src={close_icon} alt="" />
        </button>
      </header>
      <div className={styles.scroll_area}>
        <form className={styles.column} noValidate onSubmit={onSubmit}>
          <div className={styles.step_header}>
            <div className={styles.badge_row}>
              <div className={styles.badge}>{t(step.badgeKey)}</div>
              <span className={styles.counter}>{counter}</span>
            </div>
            <div className={styles.progress_track}>
              <div
                className={styles.progress_fill}
                style={{ width: `${step.progress}%` }}
              />
            </div>
          </div>
          <div className={styles.title_block}>
            <h1 className={styles.title}>{title}</h1>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
          {children}
          <button
            type="submit"
            className={styles.implicit_submit}
            tabIndex={-1}
            aria-hidden="true"
            // A disabled default button also disables implicit submission,
            // matching the disabled Next.
            disabled={!canAdvance}
          />
        </form>
      </div>
      <footer className={styles.bottom_bar}>
        <div className={styles.bottom_row}>
          <button
            type="button"
            className={styles.skip_link}
            disabled={saving}
            onClick={skip}
          >
            {t("Skip for now")}
          </button>
          <div className={styles.footer_buttons}>
            <ButtonVariant
              title={t("Back")}
              buttonType="light-primary"
              buttonStyles={styles.footer_button}
              // Disabled while saving: the overlay only blocks pointers, so a
              // keyboard user could otherwise Back out of the step mid-save.
              disabled={saving}
              onClickFunc={goBack}
            />
            <ButtonVariant
              title={t(nextLabel || "Next")}
              buttonType="primary-filled"
              buttonStyles={styles.next_button}
              disabled={saving || loading || !(nextStep || onBeforeNext)}
              loading={saving}
              // The !saving guard stays even with disabled: ButtonVariant
              // keeps its onClick attached while loading-but-not-disabled,
              // so the prop alone is not what blocks re-entry.
              onClickFunc={canAdvance ? goToNextStep : undefined}
            />
          </div>
        </div>
      </footer>
      {/* Covers the content while a step saves: blurs it, blocks every
          control (bottom bar included) and centers the spinner. */}
      {saving && (
        <div className={styles.saving_overlay}>
          <Loading isLoading loadType="spinner" />
        </div>
      )}
    </div>
  );
};

/**
 * The auth gate sits outside the step content so its step-viewed / abandoned
 * effect only ever runs for a signed-in coach. With the gate inside, a
 * logged-out hit on a step URL recorded a view (and an abandonment on the
 * redirect) before the sign-in page appeared, polluting the wizard funnel.
 */
const AcademyWizardLayout = (props: AcademyWizardLayoutProps) => (
  <Authenticated>
    <AcademyWizardGuard>
      <AcademyWizardLayoutContent {...props} />
    </AcademyWizardGuard>
  </Authenticated>
);

export default memo(AcademyWizardLayout);
